import { useState } from "react";

import { Folder, Trash2 } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@needl-ai/common/components/ui/tooltip";

import type { AsyncAnswerStatus } from "@/hooks/useAsyncSessionTracker";

interface ListItemProps {
  title: string;
  type: "chat" | "project";
  time?: string;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
  onHover?: () => void;
  asyncStatus?: AsyncAnswerStatus;
}

const ListItem = ({
  title,
  type,
  time: _time,
  isActive = false,
  disabled = false,
  onClick,
  onDelete,
  onHover,
  asyncStatus,
}: ListItemProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    // Don't execute onClick if disabled
    if (disabled) {
      return;
    }

    // Only execute onClick if this item is not already active
    if (onClick && !isActive) {
      onClick();
    }
  };

  const handleMouseEnter = () => {
    // Don't set hover state if disabled
    if (disabled) {
      return;
    }

    // Only set hover state if not active
    if (!isActive) {
      setIsHovered(true);
      if (onHover) {
        onHover();
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Don't execute delete if disabled
    if (disabled) {
      return;
    }

    if (onDelete) {
      onDelete();
    }
  };

  return (
    <div
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`group/item flex items-center gap-3 p-2 rounded-lg transition-all duration-200 ${
        isActive
          ? "cursor-default"
          : disabled
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer"
      } ${
        isActive
          ? "bg-gray-200/80 dark:bg-neutral-800"
          : !disabled && isHovered
            ? "bg-gray-100/60 dark:bg-neutral-800/70"
            : !disabled
              ? "hover:bg-gray-100/80 dark:hover:bg-neutral-800"
              : ""
      } ${isHovered && !isActive && !disabled ? "scale-[0.99] shadow-sm" : ""}`}
    >
      <div className="flex-shrink-0">
        {type === "project" && (
          <Folder
            size={16}
            className={`text-blue-600 dark:text-neutral-400 ${disabled ? "opacity-50" : ""}`}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className={`text-sm truncate font-normal ${
                  isActive || isHovered ? "font-medium" : ""
                } ${
                  isActive || isHovered
                    ? "text-gray-900 dark:text-white"
                    : "text-gray-700 dark:text-gray-200"
                }`}
              >
                {title}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" align="start">
              {title}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex-shrink-0 flex items-center gap-1 ml-1">
        {asyncStatus && (
          <span
            className={`w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 ${
              asyncStatus === "in_progress" ? "animate-pulse" : ""
            }`}
          />
        )}
        {onDelete && !disabled && (
          <button
            onClick={handleDelete}
            className="opacity-0 group-hover/item:opacity-100 transition-opacity p-1 rounded hover:bg-gray-200 dark:hover:bg-neutral-700"
            data-testid="delete-session-button"
          >
            <Trash2
              size={14}
              className="hover:text-red-400 text-gray-500 dark:text-neutral-500"
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default ListItem;
